import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { AdminUserRecord, CreateAdminUserRequest, UpdateAdminUserRequest } from '../../services/api';
import apiService from '../../services/api';

// Users state interface
export interface UsersState {
    users: AdminUserRecord[];
    selectedUser: AdminUserRecord | null;
    isLoading: boolean;
    error: string | null;
}

const initialState: UsersState = {
    users: [],
    selectedUser: null,
    isLoading: false,
    error: null,
};

// Async thunks
export const fetchUsers = createAsyncThunk(
    'users/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const users = await apiService.getAllUsers();
            return users;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch users');
        }
    }
);

export const createUser = createAsyncThunk(
    'users/create',
    async (userData: CreateAdminUserRequest, { rejectWithValue }) => {
        try {
            const newUser = await apiService.createAdminUser(userData);
            return newUser;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to create user');
        }
    }
);

export const fetchPendingUsers = createAsyncThunk(
    'users/fetchPending',
    async (_, { rejectWithValue }) => {
        try {
            const users = await apiService.getPendingUsers();
            return users;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch pending users');
        }
    }
);

export const approveUser = createAsyncThunk(
    'users/approve',
    async (id: string, { rejectWithValue }) => {
        try {
            const approvedUser = await apiService.approveAdminUser(id);
            return approvedUser;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to approve user');
        }
    }
);

export const rejectUser = createAsyncThunk(
    'users/reject',
    async (id: string, { rejectWithValue }) => {
        try {
            const rejectedUser = await apiService.rejectAdminUser(id);
            return rejectedUser;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to reject user');
        }
    }
);

export const fetchUserById = createAsyncThunk(
    'users/fetchById',
    async (id: string, { rejectWithValue }) => {
        try {
            const user = await apiService.getAdminUserById(id);
            return user;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch user details');
        }
    }
);

export const updateUser = createAsyncThunk(
    'users/update',
    async ({ id, userData }: { id: string; userData: UpdateAdminUserRequest }, { rejectWithValue }) => {
        try {
            const updatedUser = await apiService.updateAdminUser(id, userData);
            return updatedUser;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update user');
        }
    }
);

export const deleteUser = createAsyncThunk(
    'users/delete',
    async (id: string, { rejectWithValue }) => {
        try {
            await apiService.deleteAdminUser(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete user');
        }
    }
);

// Users slice
const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        clearUsersError: (state) => {
            state.error = null;
        },
        clearSelectedUser: (state) => {
            state.selectedUser = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUsers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUsers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.users = action.payload;
                state.error = null;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(createUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.users.push(action.payload);
                state.error = null;
            })
            .addCase(createUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchPendingUsers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchPendingUsers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.users = action.payload;
                state.error = null;
            })
            .addCase(fetchPendingUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(approveUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(approveUser.fulfilled, (state, action) => {
                state.isLoading = false;
                // Update the approved user in the array (e.g. status changes to active)
                const index = state.users.findIndex(u => u.id === action.payload.id);
                if (index !== -1) {
                    state.users[index] = action.payload;
                }
                state.error = null;
            })
            .addCase(approveUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(rejectUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(rejectUser.fulfilled, (state, action) => {
                state.isLoading = false;
                // Update the rejected user in the array
                const index = state.users.findIndex(u => u.id === action.payload.id);
                if (index !== -1) {
                    state.users[index] = action.payload;
                }
                state.error = null;
            })
            .addCase(rejectUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchUserById.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.selectedUser = null;
            })
            .addCase(fetchUserById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.selectedUser = action.payload;
                state.error = null;
            })
            .addCase(fetchUserById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(updateUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.isLoading = false;
                // Update the user in the list array
                const index = state.users.findIndex(u => u.id === action.payload.id);
                if (index !== -1) {
                    state.users[index] = action.payload;
                }
                // If it's the currently selected user, update it there too
                if (state.selectedUser?.id === action.payload.id) {
                    state.selectedUser = action.payload;
                }
                state.error = null;
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(deleteUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteUser.fulfilled, (state, action) => {
                state.isLoading = false;
                // Remove the user from the list
                state.users = state.users.filter(u => u.id !== action.payload);
                // Clear selectedUser if the deleted user was currently viewed
                if (state.selectedUser?.id === action.payload) {
                    state.selectedUser = null;
                }
                state.error = null;
            })
            .addCase(deleteUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearUsersError, clearSelectedUser } = usersSlice.actions;

export const selectUsers = (state: { users: UsersState }) => state.users.users;
export const selectSelectedUser = (state: { users: UsersState }) => state.users.selectedUser;
export const selectUsersLoading = (state: { users: UsersState }) => state.users.isLoading;
export const selectUsersError = (state: { users: UsersState }) => state.users.error;

export default usersSlice.reducer;
